import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import { Icon, Checkbox, Modal, Input } from "semantic-ui-react";

import withFixedColumns from "react-table-hoc-fixed-columns";
import ReactTable from "react-table-6";

// UI
import ModLabReport from "react-lib/apps/HISV3/LAB/ModLabReport";
import CardMeLabReportUX from "react-lib/apps/HISV3/LAB/CardMeLabReportUX";
import ModAttachFile from "react-lib/apps/HISV3/LAB/ModAttachFile";
import ButtonGenMeLabVideo from "./ButtonGenMeLabVideo";
import { DisplayResult } from "react-lib/apps/HISV3/LAB/CardOPDLabSummary";
import ButtonEditRemark from "react-lib/apps/HISV3/TPD/ButtonEditRemark";
import { ExpanderName, ExpandingTable } from "react-lib/apps/HISV3/LAB/CardLabReport";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ModAuthen from "react-lib/apps/common/ModAuthen";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PasswordRecheck from "react-lib/apps/common/PasswordRecheck";

// Interface
import { State, RunSequence } from "react-lib/apps/HISV3/LAB/sequence/LabReport";
import { getRefValue, getStatus } from "react-lib/apps/HISV3/LAB/sequence/OPDLabSummary";
import {
  formatLabOrderItems,
  getLabOrderItems,
  getLabOrdersReport,
  LAB_STATUS,
} from "react-lib/apps/HISV3/LAB/LabInterface";

// Utils
import { formatDatetime } from "react-lib/utils/dateUtils";

// Config
import CONFIG from "config/config";
import { useIntl } from "react-intl";
import { formatBirthdateAD } from "./CustomerInfoHeader";

// Types
type CardMeLabReportProps = {
  setProp: (key: string, value: any) => any;
  onEvent: (e: any) => any;
  // seq
  runSequence: RunSequence;
  LabReportSequence?: State["LabReportSequence"];
  // data
  selectedPatient?: Record<string, any>;
  selectedLabOrderWorking: any;
  permissionLabResultConfidential?: boolean;
  patientInfo?: any;
  titleName?: string;
  // CommonInterface
  buttonLoadCheck?: any;
  errorMessage?: any;
  searchedItemListWithKey?: Record<string, any>;
  successMessage?: any;
  // options
  masterOptions?: Record<string, any>;
  // config
  showPatientInfo?: boolean;
  // callback
  onClose?: (changed: boolean) => any;
};

// Const
const ReactTableFixedColumns = withFixedColumns(ReactTable);

// Styles
const tableStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "4px 8px 0",
  margin: 0,
};

const tableCellStyle = {
  display: "flex",
  alignItems: "center",
  padding: "4px 8px 0",
  margin: 0,
};

const IMAGES = {
  notebookBlue: "/static/images/notebook-regular-blue.png",
  notebookGrey: "/static/images/notebook-regular-grey.png",
};

const CARD_ME_LAB_REPORT = "CardMeLabReport";
const MOD_ATTACH_FILE = "ModAttachfile";

const MAX_REMARK_LENGTH = 200;

const CardMeLabReport = (props: CardMeLabReportProps) => {
  const intl = useIntl();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [selectItemExpand, setSelectItemExpand] = useState<any>({});

  const [openModAttachFile, setOpenModAttachFile] = useState<boolean>(false);
  const ocrFileInputRef = useRef<HTMLInputElement>(null);
  const [modLabReportData, setModLabReportData] = useState<any>({
    openLabReport: false,
    openAuthen: false,
    lab_order_item_id: null,
  });

  const [labOrderItems, setLabOrderItems] = useState<any[]>([]);
  const [packageOrderId, setPackageOrderId] = useState<string>("");
  const [isChangedStatus, setIsChangedStatus] = useState(false);
  const [openEditRemark, setOpenEditRemark] = useState(false);
  const [remark, setRemark] = useState<string>("");

  // Effect
  useEffect(() => {
    props.runSequence({ sequence: "LabReport", restart: true });

    return () => {
      props.runSequence({ sequence: "LabReport", clear: true });
    };
  }, []);

  useEffect(() => {
    const paymentStatus = props.selectedLabOrderWorking?.payment_status_name;
    const items = getLabOrderItems(props.LabReportSequence?.centralLabResult?.items || [], {
      check: false,
      paymentStatus,
    });

    items.forEach((item: any, parent_idx: any) => {
      const value = labOrderItems[parent_idx]?.central_lab_result_value;

      if (typeof value === "string") {
        item.central_lab_result_value = value;
      }

      item.children.forEach((acc, child_index) => {
        const value = labOrderItems[parent_idx]?.children?.[child_index]?.central_lab_result_value;

        if (typeof value === "string") {
          acc.central_lab_result_value = value;
        }
      });
    });
    setPackageOrderId(props.LabReportSequence?.centralLabResult?.package_order_id?.[0] || "");
    setLabOrderItems(items);
  }, [
    props.LabReportSequence?.centralLabResult,
    props.selectedLabOrderWorking?.payment_status_name,
  ]);

  useEffect(() => {
    if (props.successMessage?.[MOD_ATTACH_FILE]) {
      setOpenModAttachFile(false);
      props.setProp(`successMessage.${MOD_ATTACH_FILE}`, null);
    }
  }, [props.successMessage?.[MOD_ATTACH_FILE]]);

  useEffect(() => {
    if (props.successMessage?.[CARD_ME_LAB_REPORT]) {
      setUsername("");
      setPassword("");
    }
  }, [props.successMessage?.[CARD_ME_LAB_REPORT]]);

  // Fetch report ของ lab order นี้ตอนเปิด card + poll ทุก 15 วิ
  useEffect(() => {
    const centralLabOrderId = props.selectedLabOrderWorking?.id;

    if (!centralLabOrderId) return;

    const fetchReport = () =>
      props.runSequence({
        sequence: "LabReport",
        action: "FETCH_MELAB_REPORT",
        centralLabOrderId,
      });

    fetchReport();
    const intervalId = setInterval(fetchReport, 15000);

    return () => clearInterval(intervalId);
  }, [props.selectedLabOrderWorking?.id]);

  useEffect(() => {
    if (
      modLabReportData.openAuthen &&
      !modLabReportData.openLabReport &&
      props.permissionLabResultConfidential
    )
      setModLabReportData({
        ...modLabReportData,
        openLabReport: true,
        openAuthen: false,
      });
  }, [props.permissionLabResultConfidential]);

  const handleSaveComment = (original: Record<string, any>) => (data: Record<string, any>) => {
    props.runSequence({
      sequence: "LabReport",
      action: "SAVE_COMMENT",
      data: original,
      ...data,
    });
  };

  // Callback
  const handleCheckboxSelected = useCallback(
    (item: any) => (e: any, data: any) => {
      let items = [...labOrderItems];
      const patientIndex = item["parent_idx"];

      //* เมื่อ checked ตัว children
      if ("parent_idx" in item) {
        items?.forEach((it: any) => {
          it?.children?.forEach((ch: any) => {
            if (ch.central_lab_order_item_id === item.central_lab_order_item_id) {
              ch.chk = data.checked;
            }
          });
        });

        // items[patientIndex]?.children?.forEach((it: any) => {
        //   if (it.central_lab_order_item_id === item.central_lab_order_item_id) {
        //     it.chk = data.checked;
        //   }
        // });

        const children: any[] = items[patientIndex]?.children || [];
        const isCheckAll: boolean = children.every((acc: any) => acc.chk);

        items[patientIndex].chk = isCheckAll;
      }
      // เมื่อ checked parent
      else {
        items = items.map((acc) =>
          acc.central_lab_order_item_id === item.central_lab_order_item_id
            ? {
                ...acc,
                chk: data.checked,
                children: acc.children.map((o: any) => ({
                  ...o,
                  chk: data.checked,
                })),
              }
            : acc,
        );
      }

      setLabOrderItems([...items]);
    },
    [labOrderItems],
  );

  const handleChangeValue = useCallback(
    (item: any) => (e: any, v: any) => {
      let items = [...labOrderItems];

      if ("parent_idx" in item) {
        items[item["parent_idx"]]?.children?.forEach((it: any) => {
          if (it.central_lab_order_item_id === item.central_lab_order_item_id) {
            it["central_lab_result_value"] = v.value;
          }
        });
      } else {
        items = items.map((acc) =>
          acc.central_lab_order_item_id === item.central_lab_order_item_id
            ? { ...acc, central_lab_result_value: v.value }
            : acc,
        );

        item.central_lab_result_value = v.value;
      }

      setLabOrderItems(items);
    },
    [labOrderItems],
  );

  const handelCheckedAll = useCallback(
    (e: any, v: any) => {
      let items: any[] = [...labOrderItems];

      items = items.map((item) => ({
        ...item,
        chk: item.allow_editing ? v.checked : false,
        children: item.children.map((acc: any) => ({
          ...acc,
          chk: acc.allow_editing ? v.checked : false,
        })),
      }));

      setLabOrderItems(items);
    },
    [labOrderItems],
  );

  const renderCheckboxCell = useCallback(
    ({ original, index }: any) => {
      return (
        original.allow_editing && (
          <div>
            <Checkbox
              id={`CardLabReport-Checkbox-Selected-${index}`}
              checked={original.chk}
              onClick={handleCheckboxSelected(original)}
            />
          </div>
        )
      );
    },
    [labOrderItems],
  );

  const renderFileCell = useCallback(({ original }: any) => {
    return (
      <div
        style={{
          display: original.children?.length > 0 ? "none" : "flex",
          alignItems: "center",
        }}
      >
        <Icon
          name="file pdf outline"
          color="blue"
          size="large"
          style={{
            display:
              original.summary_file_result?.filter((item: any) => item.active)?.length === 0
                ? "none"
                : "",
            cursor: "pointer",
          }}
          onClick={() => {
            props.setProp("permissionLabResultConfidential", false);

            setModLabReportData({
              openLabReport: !original.is_confidential,
              openAuthen: original.is_confidential,
              lab_order_item_id: original?.central_lab_order_item_id,
            });
          }}
        ></Icon>
      </div>
    );
  }, []);

  const renderResultCell = useCallback(
    ({ original, index }: any) => {
      const handleRef = (ref: any) => {
        if (ref) {
          ref.inputRef.current.style.backgroundColor =
            original?.central_lab_order_item_status === LAB_STATUS.REPORTED ? "transparent" : "";
        }
      };

      return (
        <div
          style={{
            display: original.children?.length > 0 ? "none" : "flex",
            width: "100%",
            alignItems: "center",
          }}
        >
          <Input
            id={`CardLabReport-Input-Value-${index}`}
            ref={handleRef}
            value={original.central_lab_result_value || ""}
            fluid={true}
            disabled={!original.allow_editing}
            style={{ width: "67.5%", opacity: 1 }}
            onChange={handleChangeValue(original)}
          ></Input>
          <ButtonEditRemark
            onEvent={props.onEvent}
            setProp={props.setProp}
            id={original.id}
            label="ระบุข้อความ"
            value={original.comment}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            onSave={handleSaveComment(original)}
            trigger={
              <img
                alt="Notebook regular icon"
                src={original.comment ? IMAGES.notebookBlue : IMAGES.notebookGrey}
                style={{ cursor: "pointer", margin: "0 -3px -3px 3px", width: "20px" }}
              />
            }
          />
          <DisplayResult
            status={original.result_status}
            critical={original.result_critical}
            paddingStyle={0}
            labelStyle={{ fontSize: "0.525rem", marginTop: "-2px" }}
            statusStyle={{ marginLeft: "-0.45rem" }}
          />
        </div>
      );
    },
    [labOrderItems, props.buttonLoadCheck, props.errorMessage],
  );

  const renderNameExpander = useCallback(({ isExpanded, ...rest }: any) => {
    return <ExpanderName isExpanded={isExpanded} original={rest.original} />;
  }, []);

  const renderReferenceCell = useCallback(
    ({ original }: any) => {
      return (
        <div style={{ display: original.children?.length > 0 ? "none" : "" }}>
          {getRefValue(original.ref_value_txt, props.patientInfo?.gender_name)}
        </div>
      );
    },
    [props.patientInfo],
  );

  const renderUnitCell = useCallback(({ original }: any) => {
    return (
      <div style={{ display: original.children?.length > 0 ? "none" : "" }}>{original.unit}</div>
    );
  }, []);

  const renderStatusCell = useCallback(({ original }: any) => {
    const status =
      original.central_lab_order_item_status === LAB_STATUS.REPORTED &&
      !original.central_lab_result_is_authorized
        ? LAB_STATUS.PENDING_CONFIRM
        : original.central_lab_order_item_status;

    return (
      <div
        style={{
          display: original.children?.length > 0 ? "none" : "",
          color: status === LAB_STATUS.SPECIMEN_REJECTED ? "red" : "",
        }}
      >
        {status}
      </div>
    );
  }, []);

  const renderReporterCell = useCallback(({ original }: any) => {
    const reporter = original.central_lab_result_report_user_fullname;
    return <div>{reporter}</div>;
  }, []);

  const renderApproveCell = useCallback(({ original }: any) => {
    const authorize_user = original.central_lab_result_authorize_user_fullname;
    return <div>{authorize_user}</div>;
  }, []);

  // Memo
  const disabledAttachFile = useMemo(() => {
    const filterItems = labOrderItems
      .flatMap((item: any) => (item.children.length ? item.children : [item]))
      .filter((item: any) => item.chk);

    return !filterItems?.length;
  }, [labOrderItems]);

  const isCheckedAll = useMemo(() => {
    const editableItems = labOrderItems
      .flatMap((item: any) => (item.children.length ? item.children : [item]))
      .filter((item) => item.allow_editing);

    return !!editableItems.length && editableItems.every((item: any) => item.chk);
  }, [labOrderItems]);

  const formattedLabOrderItems = useMemo(
    () => formatLabOrderItems(labOrderItems, props.patientInfo?.gender_name || "", true),
    [labOrderItems],
  );

  const labOrdersReported = useMemo(
    () => getLabOrdersReport(formattedLabOrderItems),
    [formattedLabOrderItems],
  );

  const tableColumns = useMemo(() => {
    return [
      {
        id: "checkbox",
        accessor: "",
        Cell: ({ original }: any) => {
          return (
            original.allow_editing && (
              <div>
                <Checkbox
                  id={`CardLabReport-Checkbox-Selected-${original.central_lab_order_item_id}`}
                  checked={original.chk}
                  onClick={handleCheckboxSelected(original)}
                />
              </div>
            )
          );
        },
        Header: (
          <Checkbox
            id="CardLabReport-Checkbox-All"
            checked={isCheckedAll}
            onClick={handelCheckedAll}
          />
        ),
        sortable: false,
        width: 30,
        style: tableStyle,
        resizable: false,
      },
      {
        Header: "Lab Test",
        minWidth: 180,
        resizable: false,
        expander: true,
        style: { ...tableCellStyle, whiteSpace: "unset" },

        Expander: ({ original }: any) => {
          if (original.isHeader) {
            return (
              <div style={{ fontWeight: "bold", textDecoration: "underline" }}>
                {original.header}
              </div>
            );
          }

          return renderNameExpander({ original });
        },

        getProps: (state: any, rowInfo: any) => {
          if (rowInfo) {
            if (
              !rowInfo.original.children ||
              rowInfo.original.children.length === 0 ||
              rowInfo.original.isHeader
            ) {
              return { onClick: () => {} };
            }
          }

          return { className: "show-pointer" };
        },
      },
      {
        id: "file",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderFileCell({ original });
        },
        Header: "File",
        sortable: false,
        width: 60,
        style: tableStyle,
        resizable: false,
      },
      {
        id: "value",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderResultCell({ original });
        },
        Header: "Value",
        sortable: false,
        width: 180,
        style: tableStyle,
        resizable: false,
      },
      {
        id: "reference",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderReferenceCell({ original });
        },
        Header: "Reference",
        sortable: false,
        width: 120,
        style: tableCellStyle,
        resizable: false,
      },
      {
        id: "unit",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderUnitCell({ original });
        },
        Header: "Unit",
        sortable: false,
        width: 120,
        style: tableCellStyle,
        resizable: false,
      },
      {
        id: "status",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderStatusCell({ original });
        },
        Header: "Status",
        sortable: false,
        minWidth: 150,
        style: tableCellStyle,
        resizable: false,
      },
      {
        id: "report_user",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderReporterCell({ original });
        },
        Header: "ผู้รายงานผล",
        sortable: false,
        minWidth: 140,
        style: tableCellStyle,
        resizable: false,
      },
      {
        id: "authorize_user",
        accessor: "",
        Cell: ({ original }: any) => {
          if (original.isHeader) return null;

          return renderApproveCell({ original });
        },
        Header: "ผู้ตรวจสอบผล",
        sortable: false,
        minWidth: 140,
        style: tableCellStyle,
        resizable: false,
      },
    ];
  }, [isCheckedAll, labOrderItems, props.patientInfo, renderResultCell]);

  // Handler
  const handleCloseModLabReport = () => {
    props.setProp("permissionLabResultConfidential", false);

    props.setProp("LabReportSequence.selectedLabReport", null);

    setModLabReportData({
      openLabReport: false,
      openAuthen: false,
      lab_order_item_id: null,
    });
  };

  const handleOnAddFile = (data: any) => {
    props.runSequence({
      sequence: "LabReport",
      action: "attach_file",
      card: MOD_ATTACH_FILE,
      buttonLoadKey: `${MOD_ATTACH_FILE}_SAVE`,
      ...data,
    });
  };

  const handleRemoveItem = (index: number) => {
    let tmp = [...labOrderItems];
    tmp[index] = {
      ...tmp[index],
      chk: false,
    };
    tmp[index].children?.forEach((it: any) => {
      it["chk"] = false;
    });

    setLabOrderItems(tmp);
  };

  const handleEnterToken = (code: string) => {
    props.runSequence({
      sequence: "LabReport",
      action: "token",
      code: code,
    });
  };

  const handleCloseModAttachFile = () => {
    setOpenModAttachFile(false);
  };

  const handelUpdateLabOrderQueue = () => {
    props.onEvent({
      message: "GetLabOrderQueue",
      params: {
        action: "search",
        hn: props.selectedPatient?.hn,
      },
    });

    setIsChangedStatus(true);
  };

  const handleEdit = () => {
    props.runSequence({
      sequence: "LabReport",
      action: "EDIT",
      card: CARD_ME_LAB_REPORT,
      buttonLoadKey: `${CARD_ME_LAB_REPORT}_SAVE`,
      labOrderItems: labOrderItems,
      // username: username,
      password: password,
      onUpdated: () => {
        handelUpdateLabOrderQueue();
      },
    });
  };

  const handleAuthorize = () => {
    props.runSequence({
      sequence: "LabReport",
      action: "AUTHORIZE",
      card: CARD_ME_LAB_REPORT,
      buttonLoadKey: `${CARD_ME_LAB_REPORT}_AUTHORIZE`,
      labOrderItems: labOrderItems,
      // username: username,
      password: password,
      onUpdated: () => {
        handelUpdateLabOrderQueue();
      },
    });
  };

  const handlePrintLabReport = () => {
    props.runSequence({
      sequence: "LabReport",
      action: "PRINT_LAB_REPORT",
      data: labOrdersReported,
    });
  };

  const handlePrintLabResult = () => {
    props.runSequence({
      sequence: "LabReport",
      action: "PRINT_LAB_RESULT",
      card: CARD_ME_LAB_REPORT,
      data: labOrdersReported,
      packageOrderId: packageOrderId,
      selectedLabOrder: props.selectedLabOrderWorking,
      selectedPatient: props.patientInfo,
    });
  };

  const handleOCRScanner = () => {
    ocrFileInputRef.current?.click();
  };

  const handleOCRFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    e.target.value = "";

    props.runSequence({
      sequence: "LabReport",
      action: "OCR_UPLOAD",
      card: CARD_ME_LAB_REPORT,
      buttonLoadKey: `${CARD_ME_LAB_REPORT}_OCR_UPLOAD`,
      file,
      patient: props.selectedPatient?.id,
    });
  };

  const handleClose = () => {
    props.onClose?.(isChangedStatus);
  };

  const savedRemark = useMemo(
    () => (props.selectedLabOrderWorking?.result_remark || "").trim(),
    [props.selectedLabOrderWorking?.result_remark]
  );

  const handleEditRemark = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.slice(0, MAX_REMARK_LENGTH);

    setRemark(value);
  };

  const handleOpenEditRemark = () => {
    const statusRemark = !openEditRemark;

    if (statusRemark) {
      setRemark(props.selectedLabOrderWorking?.result_remark || "");
    }

    setOpenEditRemark(statusRemark);
  }

  const handleSaveResultRemark = () => {
    props.runSequence({
      sequence: "LabReport",
      action: "SAVE_RESULT_REMARK",
      card: CARD_ME_LAB_REPORT,
      buttonLoadKey: `${CARD_ME_LAB_REPORT}_SAVE_REMARK`,
      resultRemark: remark,
      onSuccess: () => {
        setOpenEditRemark(false);
      },
    });
  };

  return (
    <div style={{ height: "85vh" }}>
      {/* Hidden file input for OCR */}
      <input
        ref={ocrFileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        style={{ display: "none" }}
        onChange={handleOCRFileChange}
      />

      <SnackMessage
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_ME_LAB_REPORT}`, null);
        }}
        error={
          "error" in (props.errorMessage?.[CARD_ME_LAB_REPORT] || {})
            ? props.errorMessage?.[CARD_ME_LAB_REPORT]?.error
            : props.errorMessage?.[CARD_ME_LAB_REPORT]
        }
        success={null}
        languageUX={props.languageUX}
      />

      <CardMeLabReportUX
        // Data
        patientInfo={props.patientInfo}
        birthdate={formatBirthdateAD(props.patientInfo?.birthdate)}
        selectedLabOrderWorking={props.selectedLabOrderWorking}
        arriveTime={formatDatetime(props.selectedLabOrderWorking?.arrive_time_iso, true)}
        username={username}
        password={password}
        titleName={props.titleName}
        remarkLength={remark?.length || 0}
        maxRemarkLength={MAX_REMARK_LENGTH}
        showRemarkBanner={!!savedRemark && !openEditRemark}
        labelButtonRemark={
          <>
            Remark
            {!!savedRemark && (
              <span
                id="CardMeLabReport-Badge-Remark"
                style={{
                  position: "absolute",
                  top: "-3px",
                  right: "-3px",
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#FFC107",
                  border: "1.5px solid #FFF",
                }}
              />
            )}
          </>
        }
        // config
        disabledAttachFile={disabledAttachFile}
        disabledLabReport={!labOrdersReported?.length}
        hideButtonLabReport={!CONFIG.RAKSTHAI_LAB_REPORT_BUTTON}
        showPatientInfo={props.showPatientInfo}
        // Callback
        onAttachFile={() => setOpenModAttachFile(true)}
        changePassword={(e: any) => setPassword(e.target.value)}
        changeUsername={(e: any) => setUsername(e.target.value)}
        onLabReport={handlePrintLabReport}
        onClose={handleClose}
        openEditRemark={openEditRemark}
        onOpenEditRemark={handleOpenEditRemark}
        onCloseEditRemark={() => setOpenEditRemark(false)}
        // Element
        tableReport={
          <div style={{ width: "100%", height: "100%" }}>
            <ExpandingTable
              data={formattedLabOrderItems || []}
              columns={tableColumns}
              tableStyle={{ maxHeight: "100%" }}
              selectItemExpand={selectItemExpand}
              setSelectItemExpand={setSelectItemExpand}
            />
          </div>
        }
        remarkBanner={
          <div
            id="CardMeLabReport-Banner-Remark"
            style={{
              margin: "6px 0px 0px",
              background: "#FFFBEA",
              border: "1px solid #F5D97B",
              borderRadius: "8px",
              padding: "10px 14px",
              fontSize: "13px",
              color: "#5C4A00",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              contain: "inline-size",
            }}
          >
            <span style={{ fontWeight: "bold", marginRight: "6px" }}>Remark:</span>
            {savedRemark}
          </div>
        }
        textareaRemark={
          <textarea
            placeholder="พิมพ์หมายเหตุสำหรับผลแลปรวมของหน้านี้..."
            maxLength={MAX_REMARK_LENGTH}
            value={remark}
            onChange={handleEditRemark}
            style={{
              width: "100%",
              minHeight: "70px",
              resize: "none",
              border: "1px solid #CBB9F7",
              borderRadius: "6px",
              padding: "10px",
              fontSize: "13px",
              boxSizing: "border-box",
            }}
          />
        }
        buttonSave={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleEdit}
            // data
            paramKey={`${CARD_ME_LAB_REPORT}_SAVE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_ME_LAB_REPORT}_SAVE`]}
            // config
            disabled={!password}
            size="medium"
            color="green"
            name="SAVE"
            style={{ width: "100%", background: "#ADADAD", color: "white" }}
            title="Save"
          />
        }
        buttonAuthorize={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleAuthorize}
            // data
            paramKey={`${CARD_ME_LAB_REPORT}_AUTHORIZE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_ME_LAB_REPORT}_AUTHORIZE`]}
            // config
            disabled={!password}
            size="medium"
            name="AUTHORIZE"
            color="yellow"
            style={{ width: "100%" }}
            title="Authorize"
          />
        }
        buttonPrintLabResult={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handlePrintLabResult}
            // data
            paramKey={`${CARD_ME_LAB_REPORT}_PRINT_LAB_RESULT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_ME_LAB_REPORT}_PRINT_LAB_RESULT`]}
            // config
            disabled={!labOrdersReported?.length}
            size="medium"
            name="PRINT_LAB_RESULT"
            style={{ width: "100%", background: "#ADADAD", color: "white" }}
            title={intl.formatMessage({ id: "พิมพ์ผลแลป" })}
          />
        }
        buttonOCRScanner={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleOCRScanner}
            // data
            paramKey={`${CARD_ME_LAB_REPORT}_OCR_UPLOAD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_ME_LAB_REPORT}_OCR_UPLOAD`]}
            // config
            size="medium"
            name="OCR_UPLOAD"
            style={{ width: "100%", background: "var(--primary-theme-color)", color: "white" }}
            title={intl.formatMessage({ id: "OCR Scanner" })}
          />
        }
        buttonGenVideo={
          <ButtonGenMeLabVideo
            card={CARD_ME_LAB_REPORT}
            setProp={props.setProp}
            runSequence={props.runSequence}
            labOrderItems={formattedLabOrderItems}
            labOrdersReported={labOrdersReported}
            patientInfo={props.patientInfo}
            selectedLabOrderWorking={props.selectedLabOrderWorking}
            meLabReport={props.LabReportSequence?.meLabReport}
            meLabVideoStatus={props.LabReportSequence?.meLabVideoStatus}
            buttonLoadCheck={props.buttonLoadCheck}
          />
        }
        buttonSaveRemark={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSaveResultRemark}
            // data
            paramKey={`${CARD_ME_LAB_REPORT}_SAVE_REMARK`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_ME_LAB_REPORT}_SAVE_REMARK`]}
            // config
            size="medium"
            name="SAVE_REMARK"
            style={{ background: "#8B5CF6", color: "#FFFFFF" }}
            title="บันทึก Remark"
          />
        }
        inputPassword={
          <PasswordRecheck
            style={{ maxWidth: "15rem" }}
            password={password}
            setPassword={setPassword}
            placeholder={"ระบุรหัสผู้ใช้งาน"}
          />
        }
        languageUX={props.languageUX}
      />

      <ModAttachFile
        setProp={props.setProp}
        // data
        open={openModAttachFile}
        labOrderItems={labOrderItems}
        errorMessage={props.errorMessage}
        buttonLoadCheck={props.buttonLoadCheck}
        labReportToken={props.LabReportSequence?.LabReportToken || {}}
        // callback
        onRemoveItem={handleRemoveItem}
        onEnterToken={handleEnterToken}
        onClose={handleCloseModAttachFile}
        onSave={handleOnAddFile}
        languageUX={props.languageUX}
      />

      <ModAuthen
        titleName={intl.formatMessage({
          id: "อนุญาตให้เข้าถึงผลการทดสอบทางห้องปฏิบัติการ เฉพาะบุคคลที่มีสิทธิ์เท่านั้น",
        })}
        titleColor="blue"
        open={modLabReportData?.openAuthen}
        onDeny={handleCloseModLabReport}
        onApprove={({ username, password }: any) => {
          props.onEvent({
            message: "CheckPermissionLabConfidential",
            params: {
              username: username,
              password: password,
              cardKey: CARD_ME_LAB_REPORT,
            },
          });
        }}
      />

      <Modal
        open={modLabReportData?.openLabReport}
        style={{
          width: "80%",
          padding: "1rem 1rem",
          margin: "1rem 0 0",
          height: "90vh",
          overflowY: "auto",
        }}
        onClose={handleCloseModLabReport}
      >
        <ModLabReport
          // function
          onEvent={props.onEvent}
          setProp={props.setProp}
          // seq
          runSequence={props.runSequence}
          LabReportSequence={props.LabReportSequence}
          // options
          masterOptions={props.masterOptions}
          // data
          permissionLabResultConfidential={props.permissionLabResultConfidential}
          cardKey={CARD_ME_LAB_REPORT}
          // CommonInterface
          errorMessage={props.errorMessage}
          buttonLoadCheck={props.buttonLoadCheck}
          patientInfo={props.patientInfo}
          selectedLabOrderWorking={props.selectedLabOrderWorking}
          selectedLabOrderItem={modLabReportData?.lab_order_item_id}
          searchedItemListWithKey={props.searchedItemListWithKey}
          // callback
          onModLabReport={handleCloseModLabReport}
          languageUX={props.languageUX}
        />
      </Modal>
    </div>
  );
};

CardMeLabReport.displayName = "CardMeLabReport";

export default memo(CardMeLabReport);
